import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { CustomerApi } from '_api';
import { ICON_ALIASES } from '_constants/icons';
import { theme } from '_constants/theme';
import { handleError, handleResponse } from '_helpers/api';
import { selectMainProduct, selectData as selectOrderDetails } from '_store/order/selector';
import { getOrder } from '_store/order/slice';
import { selectCustomerId } from '_store/user/selector';

import Spinner from 'components/shared/Spinner';
import Tabs from 'components/shared/Tabs';
import Text from 'components/shared/layouts/Text';

const StyledContainer = styled.div`
  padding-top: ${theme.spaces.large}px;
  position: relative;

  @media ${theme.devices.tablet} {
    padding-top: ${theme.spaces.regular}px;
    padding-bottom: ${theme.spaces.regular}px;
  }
`;

const StyledNavContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spaces.regular}px;
`;

const StyledTabsContainer = styled.div`
  margin-bottom: ${theme.spaces.regular}px;
`;

const StyledLinkContainer = styled.div`
  cursor: pointer;

  *:hover {
    color: ${theme.colors.cyanBlue};
  }
`;

const StyledMessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 120px;
  height: calc(100vh - 184px);
  width: 100%;
  padding: ${theme.spaces.regular}px;
  background-color: ${theme.colors.white};

  @media ${theme.devices.tablet} {
    top: 120px;
    height: calc(100vh - 192px);
  }
`;

const QuestionnaireView = () => {
  const iframeRef = useRef();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const customerId = useSelector(selectCustomerId);
  const orderDetails = useSelector(selectOrderDetails);
  const mainProduct = useSelector(selectMainProduct);
  const { orderId } = useParams();
  const [voucherData, setVoucherData] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleBack = () => {
    navigate(-1);
  };

  const handleTabSelect = useCallback(
    (tabId) => {
      if (tabId === 'order-details') {
        navigate(`../order/${orderId}`);
      }
      if (tabId === 'chat') {
        navigate(`../order/${orderId}/chat`);
      }
    },
    [orderId]
  );

  useEffect(() => {
    if (customerId && orderId) {
      dispatch(getOrder({ customerId, orderId }));
    }
  }, [customerId, orderId]);

  useEffect(() => {
    if (customerId && mainProduct.id) {
      (async function () {
        try {
          const response = await CustomerApi.createVoucher({ customerId, productId: mainProduct.id });
          setVoucherData(handleResponse(response));
        } catch (error) {
          toast.error(handleError(error)?.message || 'Something went wrong');
        }
      })();
    }
  }, [customerId, mainProduct]);

  useEffect(() => {
    const handleChatEvents = (e) => {
      const event = e.data.event;

      if (!event && !voucherData) {
        setLoading(true);
      } else {
        setLoading(false);
      }
    };

    window.addEventListener('message', handleChatEvents, false);

    return () => {
      window.removeEventListener('message', handleChatEvents, false);
    };
  }, [voucherData]);

  return (
    <StyledContainer>
      <StyledNavContainer>
        <StyledLinkContainer onClick={handleBack}>
          <Text color={theme.colors.gray.secondary}>&lt; Go back</Text>
        </StyledLinkContainer>
        <Text weight={700} color={theme.colors.deepBlueGreen}>
          Order: #{orderDetails?.crmOrderId}
        </Text>
      </StyledNavContainer>
      <StyledTabsContainer>
        <Tabs
          color={theme.colors.deepBlueGreen}
          selected="questionnaire"
          items={[
            {
              tabId: 'order-details',
              iconAlias: ICON_ALIASES.document,
              title: 'Order Details',
            },
            {
              tabId: 'chat',
              iconAlias: ICON_ALIASES.message,
              title: 'Chat',
            },
            {
              tabId: 'questionnaire',
              iconAlias: ICON_ALIASES.documentNormal,
              title: 'Questionnaire',
            },
          ]}
          onSelect={handleTabSelect}
        />
      </StyledTabsContainer>
      {loading && (
        <StyledMessageContainer>
          <Spinner />
          <Text color={theme.colors.gray.secondary} topSpace={theme.spaces.smallX}>
            Loading...
          </Text>
        </StyledMessageContainer>
      )}
      <iframe
        ref={iframeRef}
        src={voucherData?.onboardingUrl}
        allow="camera; microphone"
        style={{ width: '100%', height: 'calc(100vh - 184px)' }}
      />
    </StyledContainer>
  );
};

export default QuestionnaireView;
